/**
 * Recording a reply the operator sent themselves.
 *
 * Tacit learns about a conversation from the HeyReach webhook, which only ever
 * saw messages that went out through a HeyReach campaign — and has seen nothing
 * at all since the subscription lapsed on 2026-06-27. A reply typed by hand in
 * LinkedIn was invisible, so `action_state` stayed on 'response_received' and
 * the Today queue kept proposing work that was already done. Colleen Soppelsa
 * sat in that state for two months after being answered in full.
 *
 * This module is the shape of the correction: one operator statement — "I
 * replied, here, on this day" — turned into the activity row that records it
 * and the contact patch that moves the turn.
 *
 * Purity: no I/O, no clock of its own.
 */

import type { ActionState, Activity } from './types.js';

export const REPLY_CHANNELS = ['linkedin', 'email', 'call', 'other'] as const;

export type ReplyChannel = (typeof REPLY_CHANNELS)[number];

export const REPLY_CHANNEL_LABELS: Record<ReplyChannel, string> = {
  linkedin: 'LinkedIn message',
  email: 'Email',
  call: 'Call',
  other: 'Other',
};

/** How long to wait for an answer before the contact comes back to the queue. */
export const DEFAULT_FOLLOW_UP_DAYS = 7;

export interface LogReplyInput {
  /** Where the reply was sent. */
  channel: ReplyChannel;
  /** `YYYY-MM-DD`, the day it actually went out — not necessarily today. */
  sentOn: string;
  /** What was said. Optional: a reply with no transcript still moves the turn. */
  body: string;
  /** `YYYY-MM-DD` to chase on if nothing comes back. */
  followUpOn: string;
}

export interface LoggedReply {
  activity: {
    activityType: Activity['activity_type'];
    description: string;
    metadata: Record<string, unknown>;
  };
  contactUpdates: {
    action_state: ActionState;
    last_contact_date: string;
    due_date: string;
  };
}

function toIsoDate(value: Date): string {
  const year = String(value.getFullYear()).padStart(4, '0');
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * The default chase date: `DEFAULT_FOLLOW_UP_DAYS` after the reply went out,
 * rolled forward off a weekend. A chase that lands on Saturday is a chase that
 * silently becomes overdue on Monday.
 *
 * Anchored on the day the reply was sent, not on today, so logging an old reply
 * produces a date that has already passed — which is correct: Colleen's May
 * reply is overdue for a chase, and the queue should say so rather than quietly
 * granting another week.
 */
export function defaultFollowUpDate(sentOn: string, days: number = DEFAULT_FOLLOW_UP_DAYS): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(sentOn);
  if (!match) return sentOn;

  const anchor = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(anchor.getTime())) return sentOn;

  anchor.setDate(anchor.getDate() + days);
  while (anchor.getDay() === 0 || anchor.getDay() === 6) {
    anchor.setDate(anchor.getDate() + 1);
  }

  return toIsoDate(anchor);
}

const FIRST_LINE_LIMIT = 140;

/** A one-line summary for the timeline; the full text lives in the metadata. */
function summarise(body: string, channel: ReplyChannel): string {
  const collapsed = body.replace(/\s+/g, ' ').trim();
  if (collapsed === '') {
    return `Replied via ${REPLY_CHANNEL_LABELS[channel].toLowerCase()}. No transcript recorded.`;
  }
  return collapsed.length > FIRST_LINE_LIMIT
    ? `${collapsed.slice(0, FIRST_LINE_LIMIT).trimEnd()}…`
    : collapsed;
}

/**
 * Turns the operator's statement into exactly two writes: an append-only
 * activity, and the contact patch that hands the turn back.
 *
 * `action_state` always becomes 'waiting_response'. That is the whole point —
 * the operator has replied, so it is no longer their move, and the contact
 * leaves the Today queue until the follow-up date.
 */
export function buildLoggedReply(input: LogReplyInput): LoggedReply {
  const body = input.body.trim();

  return {
    activity: {
      activityType: input.channel === 'call' ? 'call' : 'message_sent',
      description: summarise(body, input.channel),
      metadata: {
        channel: input.channel,
        sent_on: input.sentOn,
        captured_by: 'operator',
        ...(body === '' ? {} : { body }),
      },
    },
    contactUpdates: {
      action_state: 'waiting_response',
      last_contact_date: input.sentOn,
      due_date: input.followUpOn,
    },
  };
}
